import React from "react";
import { TrashIcon } from "@heroicons/react/24/outline";
import Button from "../../../componentLibrary/Button";
import { ModuleI, ModuleStatus } from "../../../types";

interface ModulePreviewProps {
  module: ModuleI;
  onDeleteClick: (type: "module", id: string, title: string) => void;
}

const ModulePreview: React.FC<ModulePreviewProps> = ({
  module,
  onDeleteClick,
}) => {
  const lessonCount = module.lessons?.length || 0;

  return (
    <div className="h-full flex flex-col">
      {/* Sticky header with delete button */}
      <div className="flex items-center justify-between pb-4 border-b flex-shrink-0">
        <div className="flex items-center gap-4">
          {module.icon?.src && (
            <img
              src={module.icon.src}
              alt={module.icon.alt || module.title}
              className="w-12 h-12 object-contain"
            />
          )}
          <div>
            <h3 className="text-xl font-semibold">{module.title}</h3>
            <p className="text-sm text-gray-500">
              {lessonCount} lesson{lessonCount !== 1 ? "s" : ""}
            </p>
          </div>
        </div>
        <Button
          variant="cautionary"
          onClick={() => onDeleteClick("module", module._id, module.title)}
          className="flex items-center gap-2 flex-shrink-0"
        >
          <TrashIcon className="w-5 h-5" />
          Delete Module
        </Button>
      </div>

      {/* Scrollable content area */}
      <div className="flex-1 overflow-y-auto mt-4">
        <div className="space-y-4">
          {/* Lessons that will be unlinked */}
          <div>
            <h4 className="font-medium text-gray-700 mb-2">
              Lessons in this module:
            </h4>
            {module.lessons && module.lessons.length > 0 ? (
              <div className="space-y-2">
                {module.lessons.map((lesson, index) => (
                  <div
                    key={lesson._id}
                    className="p-3 bg-gray-50 rounded-lg border"
                  >
                    <p className="font-medium text-sm">
                      {index + 1}. {lesson.title || "Untitled Lesson"}
                    </p>
                    <p className="text-xs text-gray-500">
                      {lesson.pages?.length || 0} page
                      {lesson.pages?.length !== 1 ? "s" : ""}
                    </p>
                  </div>
                ))}
                <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-sm text-blue-800">
                    These lessons will be <strong>unlinked</strong> but not
                    deleted. They will remain in the database and can be used by
                    other modules.
                  </p>
                </div>
              </div>
            ) : (
              <p className="text-gray-500 italic text-sm">
                No lessons in this module
              </p>
            )}
          </div>

          <div>
            <h4 className="font-medium text-gray-700 mb-2">Status:</h4>
            <span
              className={`inline-block px-2 py-1 text-xs font-medium rounded ${
                module.status === ModuleStatus.Published
                  ? "bg-green-100 text-green-600"
                  : module.status === ModuleStatus.Archived
                    ? "bg-gray-100 text-gray-600"
                    : module.status === ModuleStatus.Coming_Soon
                      ? "bg-purple-100 text-purple-600"
                      : "bg-yellow-100 text-yellow-600"
              }`}
            >
              {module.status || ModuleStatus.Draft}
            </span>
          </div>

          {module.categories && module.categories.length > 0 && (
            <div>
              <h4 className="font-medium text-gray-700 mb-2">Categories:</h4>
              <div className="flex flex-wrap gap-2">
                {module.categories.map((category) => (
                  <span
                    key={category}
                    className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded"
                  >
                    {category}
                  </span>
                ))}
              </div>
            </div>
          )}

          {module.description && (
            <div>
              <h4 className="font-medium text-gray-700 mb-2">Description:</h4>
              <div
                className="text-sm text-gray-600 prose prose-sm max-w-none"
                dangerouslySetInnerHTML={{ __html: module.description }}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ModulePreview;
